import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import {
  addDays,
  fmtDate,
  fmtQty,
  localDay,
  norm,
  todayJakarta,
} from '../format';
import { type SkuOption, useClientCatalog } from '../hooks/useCatalog';
import { type FieldTarget, useServerErrors } from '../hooks/useServerErrors';
import { useWizard } from '../hooks/useWizard';
import { buildHubClientSnapshot } from '../snapshot';
import { PO_TYPE_META } from '../status';
import type {
  ClientRef,
  CreatePurchaseOrderPayload,
  FieldError,
  ManualPurchaseOrderType,
  PurchaseOrder,
  PurchaseOrderDetail,
  UpdateDraftPurchaseOrderPayload,
  VendorRef,
  WarehouseSku,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import { DateField } from './DateField';
import { FormField, TextArea, TextInput } from './form';
import { ClientSelect, HubSelect, VendorSelect } from './pickers';
import { SkuFormModal } from './SkuModals';
import {
  filledSkuLines,
  newSkuLine,
  type SkuLineDraft,
  SkuLinesEditor,
} from './SkuLinesEditor';
import { codeCls, KVGrid, metaCls } from './ui';
import { WizardFooter, WizardSteps } from './wizard';

/** Option value on the line's SKU select that opens the create-SKU modal. */
const NEW_SKU = '__new_sku__';
const NEW_SKU_OPTIONS: SkuOption[] = [{ value: NEW_SKU, label: '+ SKU baru…' }];

const STEPS = [
  { id: 1, label: 'Info PO' },
  { id: 2, label: 'Barang' },
  { id: 3, label: 'Review' },
];

type PoFieldKey =
  | 'type'
  | 'vendor'
  | 'returnedFrom'
  | 'client'
  | 'hub'
  | 'eta'
  | 'notes'
  | 'lines'
  | 'form';

const PO_STEP_OF: Record<PoFieldKey, number> = {
  type: 1,
  vendor: 1,
  returnedFrom: 1,
  client: 1,
  hub: 1,
  eta: 1,
  notes: 1,
  lines: 2,
  form: 3,
};

const poStepOfKey = (k: PoFieldKey): number => PO_STEP_OF[k];

/** Map a backend field name (snake, dotted or camel) onto a PO form field. */
export const poFieldOf = (e: FieldError): FieldTarget<PoFieldKey> => {
  const raw = e.field || '';
  const n = norm(raw);
  if (!n) return { key: 'form' };
  const lineMatch = /lines?\W*(\d+)/i.exec(raw);
  const line =
    e.lineIndex !== undefined && e.lineIndex !== null
      ? e.lineIndex
      : lineMatch
        ? Number(lineMatch[1])
        : null;
  if (line !== null || n.startsWith('line') || n === 'sku' || n === 'qty')
    return { key: 'lines', line };
  if (n === 'type') return { key: 'type' };
  if (n.includes('vendor') || n.includes('supplier')) return { key: 'vendor' };
  if (n.includes('returned')) return { key: 'returnedFrom' };
  if (n.includes('client')) return { key: 'client' };
  if (n.includes('hub')) return { key: 'hub' };
  if (n.startsWith('est') || n.includes('arrival')) return { key: 'eta' };
  if (n === 'notes') return { key: 'notes' };
  return { key: 'form' };
};

/** Code-only 4xx that are fixed on the Info step (inactive vendor, bad type). */
const PO_CODE_FIELD: Readonly<Record<string, string>> = {
  VENDOR_INACTIVE: 'vendorID',
  PO_TYPE_NOT_MANUAL: 'type',
  NOT_FOUND: 'vendorID',
};

/** What the form holds, as one string — to tell "edited" from "as loaded". */
const formSnapshot = (
  vendorID: string | null,
  returnedFrom: string,
  eta: string,
  notes: string,
  lines: SkuLineDraft[]
): string =>
  JSON.stringify([
    vendorID,
    returnedFrom.trim(),
    eta,
    notes.trim(),
    filledSkuLines(lines).map((l) => [l.sku, l.qty.trim()]),
  ]);

interface PurchaseOrderFormModalBase {
  open: boolean;
  initialHubID: number | null;
  onClose: () => void;
}

/**
 * Create (`onCreated`) or edit a DRAFT (`editing` + `onUpdated`): the edit
 * form is prefilled from the PO and its lines; hub, client and type are shown
 * but fixed, the rest is replaced on save. `onStale` = the server says the PO
 * is no longer a DRAFT (409): the page should close the form and reload.
 */
export type PurchaseOrderFormModalProps = PurchaseOrderFormModalBase &
  (
    | {
        editing?: undefined;
        onCreated: (po: PurchaseOrder) => void;
      }
    | {
        editing: PurchaseOrderDetail;
        onUpdated: (po: PurchaseOrder) => void;
        onStale: () => void;
      }
  );

/**
 * Create PO — born DRAFT; Confirm turns it into an inbound request. Three
 * steps; a SKU missing from the client's catalog can be created on the Barang
 * step in the SKU page's own create modal, stacked over this one (it lands in
 * the catalog immediately and is picked on the line). Type REGULAR picks an
 * active vendor, RETURN types who sent the goods back; TRANSFER POs are never
 * made here — they are born from an outbound transfer at the source hub.
 * Errors (local guard or server 422) show in the summary and inline, and the
 * wizard jumps to the earliest step that owns one. Edit mode (a DRAFT PO) is the
 * same form with hub / client / type read-only.
 */
const PurchaseOrderFormModal: React.FC<PurchaseOrderFormModalProps> = (
  props
) => {
  const { open, initialHubID, onClose } = props;
  const editing = props.editing ?? null;
  // The effect below reads it by ref: a reload behind the form must not reset it.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const editingID = editing?.order.id ?? null;
  const { hubs } = useWarehouse();
  const [client, setClient] = useState<ClientRef | null>(null);
  const [hubID, setHubID] = useState<number | null>(initialHubID);
  const [type, setType] = useState<ManualPurchaseOrderType>('REGULAR');
  const [vendor, setVendor] = useState<VendorRef | null>(null);
  const [returnedFrom, setReturnedFrom] = useState('');
  const [eta, setEta] = useState(addDays(todayJakarta(), 3));
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<SkuLineDraft[]>(() => [newSkuLine()]);
  /** The create-SKU modal: which line it fills ('append' = a new one) and the code typed in that line's search. */
  const [newSku, setNewSku] = useState<{
    line: number | 'append';
    code: string;
  } | null>(null);
  /** Moves focus to a line's qty (a new `seq` focuses again). */
  const [focusQty, setFocusQty] = useState<{ key: number; seq: number } | null>(
    null
  );
  const focusSeq = useRef(0);
  const [createdSkus, setCreatedSkus] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  /** Edit mode: the form as loaded (see `formSnapshot`). */
  const [baseline, setBaseline] = useState('');
  const server = useServerErrors(poFieldOf, poStepOfKey);
  const { errors, setErrors, fieldError: fe } = server;

  const filled = filledSkuLines(lines);

  const problemsOf = (s: number): FieldError[] => {
    const out: FieldError[] = [];
    if (s === 1) {
      if (!client)
        out.push({ field: 'clientID', message: 'Client wajib dipilih.' });
      if (!hubID)
        out.push({ field: 'hubID', message: 'Hub tujuan wajib dipilih.' });
      if (type === 'REGULAR' && !vendor)
        out.push({ field: 'vendorID', message: 'Vendor wajib dipilih.' });
      if (type === 'RETURN') {
        if (!returnedFrom.trim())
          out.push({
            field: 'returnedFrom',
            message: 'Diterima dari wajib diisi.',
          });
      }
    }
    if (s === 2) {
      if (!filled.length)
        out.push({ field: 'lines', message: 'Minimal satu baris SKU.' });
      filled.forEach((l, i) => {
        const q = Number(l.qty);
        if (!l.sku)
          out.push({
            field: 'lines',
            message: `Baris ${i + 1}: SKU wajib dipilih.`,
          });
        if (!Number.isFinite(q) || q <= 0)
          out.push({
            field: 'lines',
            message: `Baris ${i + 1}: qty harus > 0.`,
          });
      });
    }
    return out;
  };

  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: problemsOf,
  });
  const { step } = wizard;
  const resetWizard = wizard.reset;

  useEffect(() => {
    if (!open) return;
    resetWizard();
    const ed = editingRef.current;
    if (ed) {
      const o = ed.order;
      const t: ManualPurchaseOrderType =
        o.type === 'RETURN' ? 'RETURN' : 'REGULAR';
      const loaded = ed.lines.map((l) => ({
        ...newSkuLine(l.sku),
        qty: String(l.qty),
      }));
      const day = o.estArrivalAt ? localDay(o.estArrivalAt) : '';
      setClient(o.client ?? { id: o.clientID, name: `Client #${o.clientID}` });
      setHubID(o.hubID);
      setType(t);
      setVendor(t === 'REGULAR' ? o.vendor : null);
      // The server derives it from `returnedFrom` on a RETURN PO.
      setReturnedFrom(t === 'RETURN' ? o.supplierName : '');
      setEta(day);
      setNotes(o.notes ?? '');
      setLines(loaded.length ? loaded : [newSkuLine()]);
      setBaseline(
        formSnapshot(
          t === 'REGULAR' ? (o.vendor?.id ?? null) : null,
          t === 'RETURN' ? o.supplierName : '',
          day,
          o.notes ?? '',
          loaded
        )
      );
    } else {
      setClient(null);
      setHubID(initialHubID);
      setType('REGULAR');
      setVendor(null);
      setReturnedFrom('');
      setEta(addDays(todayJakarta(), 3));
      setNotes('');
      setLines([newSkuLine()]);
      setBaseline('');
    }
    setNewSku(null);
    setFocusQty(null);
    setCreatedSkus([]);
    setErrors([]);
    setBusy(false);
  }, [open, editingID, initialHubID, resetWizard, setErrors]);

  const catalog = useClientCatalog({
    clientID: client?.id ?? null,
    hubID: null,
    chosen: lines.map((l) => l.sku),
    enabled: open,
  });
  const openNewSku = useCallback(
    (_value: string, line: number, code: string) => setNewSku({ line, code }),
    []
  );

  if (!open) return null;

  const next = () => {
    setErrors(wizard.next());
  };

  const back = () => {
    setErrors([]);
    wizard.back();
  };

  /** The SKU modal saved: pick it on the line that asked, then on to its qty. */
  const onSkuCreated = (s: WarehouseSku) => {
    catalog.addSku(s);
    setCreatedSkus((prev) => (prev.includes(s.sku) ? prev : [...prev, s.sku]));
    const target = newSku?.line;
    const line =
      (typeof target === 'number'
        ? lines.find((l) => l.key === target)
        : undefined) ?? lines.find((l) => !l.sku);
    const added = line ? null : newSkuLine(s.sku);
    setLines((prev) =>
      added
        ? [...prev, added]
        : prev.map((l) => (l.key === line?.key ? { ...l, sku: s.sku } : l))
    );
    const key = line?.key ?? added?.key;
    if (key !== undefined) setFocusQty({ key, seq: ++focusSeq.current });
    setNewSku(null);
    toast.success('SKU ditambahkan', {
      description: `${s.sku} masuk katalog ${client?.name ?? 'client'}`,
    });
  };

  /** Validate every step, then build the payload — typed, no assertions. */
  const validate = ():
    | {
        draft: {
          source: { vendorID: string } | { returnedFrom: string };
          client: ClientRef;
          hubID: number;
          estArrivalAt: string | null;
          notes: string;
          lines: { sku: string; qty: number }[];
        };
        errors: null;
      }
    | { draft: null; errors: FieldError[] } => {
    const local = wizard.checkAll();
    const source =
      type === 'REGULAR'
        ? vendor
          ? { vendorID: vendor.id }
          : null
        : { returnedFrom: returnedFrom.trim() };
    if (local.length || !client || !hubID || !source)
      return { draft: null, errors: local };
    return {
      errors: null,
      draft: {
        source,
        client,
        hubID,
        estArrivalAt: eta ? new Date(`${eta}T10:00:00`).toISOString() : null,
        notes: notes.trim(),
        lines: filled.map((l) => ({ sku: l.sku, qty: Number(l.qty) })),
      },
    };
  };

  const submit = async () => {
    const { draft, errors: local } = validate();
    if (!draft) {
      setErrors(local);
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      if (props.editing) {
        // Hub, client and type stay as they are: the server takes none of them.
        const body: UpdateDraftPurchaseOrderPayload = {
          ...draft.source,
          estArrivalAt: draft.estArrivalAt,
          notes: draft.notes,
          lines: draft.lines,
        };
        const po = await warehouseApi.updateDraftPurchaseOrder(
          props.editing.order.id,
          body
        );
        toast.success('PO diperbarui', { description: po.code });
        props.onUpdated(po);
      } else {
        const payload: CreatePurchaseOrderPayload = {
          type,
          ...draft.source,
          ...buildHubClientSnapshot(hubs, draft.hubID, draft.client),
          estArrivalAt: draft.estArrivalAt,
          notes: draft.notes,
          lines: draft.lines,
        };
        props.onCreated(await warehouseApi.createPurchaseOrder(payload));
      }
    } catch (err) {
      // Someone confirmed or cancelled it first: nothing here can be saved.
      if (props.editing && parseWarehouseError(err).status === 409) {
        toast.error('PO tidak bisa diubah lagi', {
          description: parseWarehouseError(err).message,
        });
        props.onStale();
        return;
      }
      // 422 field errors / an inactive vendor → inline, back to their step.
      const shown = server.fromError(err, PO_CODE_FIELD);
      if (shown.step !== null) wizard.goTo(shown.step);
      else setErrors([{ field: '', message: shown.message }]);
    } finally {
      setBusy(false);
    }
  };

  const hubName =
    hubs.find((h) => h.id === hubID)?.name ??
    editing?.order.hub?.name ??
    (hubID ? `Hub #${hubID}` : null);
  const edited =
    formSnapshot(vendor?.id ?? null, returnedFrom, eta, notes, lines) !==
    baseline;
  const catalogEmpty = !!client && !catalog.error && catalog.empty;
  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        icon="ri-file-list-3-line"
        title={editing ? 'Ubah PO draft' : 'Create purchase order'}
        subtitle={editing?.order.code}
        width="xl"
        confirmOnClose={
          !busy &&
          (editing
            ? edited
            : !!client ||
              !!vendor ||
              !!returnedFrom.trim() ||
              filled.length > 0)
        }
        closeOnBackdropClick={!busy}
        closeOnEscape={!busy}
        footer={
          <WizardFooter
            step={step}
            total={STEPS.length}
            onCancel={onClose}
            onBack={back}
            onNext={next}
            onSubmit={() => void submit()}
            submitLabel={editing ? 'Simpan perubahan' : 'Simpan draft'}
            submitTestId="po-submit"
            busy={busy}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <WizardSteps steps={STEPS} current={step} />
          {errors.length > 0 && (
            <Alert
              status="error"
              appearance="stroke"
              size="lg"
              title={`${errors.length} isian perlu diperbaiki`}
              data-testid="po-errors"
            >
              <ul className="list-disc pl-4">
                {errors.map((e, i) => (
                  <li key={`${i}-${e.message}`}>{e.message}</li>
                ))}
              </ul>
            </Alert>
          )}

          {step === 1 && (
            <>
              {editing ? (
                <div className="grid grid-cols-3 gap-3">
                  <FormField label="Tipe PO">
                    <p
                      className="text-sm font-medium text-text-strong-950"
                      data-testid="po-readonly-type"
                    >
                      {PO_TYPE_META[type].label}
                    </p>
                  </FormField>
                  <FormField label="Client">
                    <p
                      className="text-sm font-medium text-text-strong-950"
                      data-testid="po-readonly-client"
                    >
                      {client?.name}
                    </p>
                  </FormField>
                  <FormField label="Hub tujuan">
                    <p
                      className="text-sm font-medium text-text-strong-950"
                      data-testid="po-readonly-hub"
                    >
                      {hubName}
                    </p>
                  </FormField>
                </div>
              ) : (
                <>
                  <FormField label="Tipe PO" required error={fe('type')}>
                    <SegmentedControl
                      value={type}
                      onValueChange={(v: string) => {
                        if (v === 'REGULAR' || v === 'RETURN') setType(v);
                      }}
                      aria-label="Tipe PO"
                      className="w-full [&>*]:flex-1"
                    >
                      {(['REGULAR', 'RETURN', 'TRANSFER'] as const).map((t) => (
                        <SegmentedItem
                          key={t}
                          value={t}
                          disabled={t === 'TRANSFER'}
                          data-testid={`po-type-${t}`}
                        >
                          <i
                            className={`${PO_TYPE_META[t].icon} text-[14px]`}
                          />
                          {PO_TYPE_META[t].label}
                        </SegmentedItem>
                      ))}
                    </SegmentedControl>
                  </FormField>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Client"
                      required
                      testId="po-client"
                      error={fe('client')}
                    >
                      <ClientSelect
                        value={client}
                        onChange={(c) => {
                          setClient(c);
                          setLines([newSkuLine()]);
                          setCreatedSkus([]);
                        }}
                        invalid={!!fe('client')}
                      />
                    </FormField>
                    <FormField label="Hub tujuan" required error={fe('hub')}>
                      <HubSelect
                        value={hubID}
                        onChange={setHubID}
                        invalid={!!fe('hub')}
                      />
                    </FormField>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                {type === 'REGULAR' ? (
                  <FormField
                    label="Vendor"
                    required
                    testId="po-vendor"
                    error={fe('vendor')}
                  >
                    <VendorSelect
                      value={vendor}
                      onChange={setVendor}
                      invalid={!!fe('vendor')}
                    />
                  </FormField>
                ) : (
                  <FormField
                    label="Diterima dari"
                    htmlFor="po-returned-from"
                    required
                    error={fe('returnedFrom')}
                  >
                    <TextInput
                      id="po-returned-from"
                      size="md"
                      value={returnedFrom}
                      onChange={setReturnedFrom}
                      invalid={!!fe('returnedFrom')}
                    />
                  </FormField>
                )}
                <FormField
                  label="Est. arrival"
                  htmlFor="po-eta"
                  error={fe('eta')}
                >
                  <DateField
                    id="po-eta"
                    value={eta}
                    onChange={setEta}
                    invalid={!!fe('eta')}
                  />
                </FormField>
              </div>
              <FormField
                label="Catatan"
                htmlFor="po-notes"
                optional
                error={fe('notes')}
              >
                <TextArea
                  id="po-notes"
                  value={notes}
                  onChange={setNotes}
                  placeholder="opsional"
                />
              </FormField>
            </>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-text-strong-950">
                  Barang · {client?.name}
                </span>
                <div className="flex items-center gap-1">
                  {!catalog.error && (
                    <Button
                      tone="neutral"
                      style="ghost"
                      size="xs"
                      onClick={() => setNewSku({ line: 'append', code: '' })}
                      leftIcon={<i className="ri-barcode-line text-[14px]" />}
                      data-testid="po-new-sku"
                    >
                      SKU baru
                    </Button>
                  )}
                  <Button
                    tone="neutral"
                    style="ghost"
                    size="xs"
                    onClick={() => setLines((p) => [...p, newSkuLine()])}
                    leftIcon={<i className="ri-add-line text-[14px]" />}
                  >
                    Tambah baris
                  </Button>
                </div>
              </div>
              {catalog.error && (
                // Unknown ≠ empty: offering "SKU baru" here would invite
                // duplicates of SKUs the catalog already has.
                <Alert
                  status="error"
                  appearance="stroke"
                  size="sm"
                  title={`Katalog SKU gagal dimuat: ${catalog.error.message}`}
                  data-testid="catalog-error"
                  action={
                    <Button
                      tone="neutral"
                      style="stroke"
                      size="xs"
                      onClick={catalog.reload}
                    >
                      Coba lagi
                    </Button>
                  }
                />
              )}
              {catalogEmpty && (
                <Alert status="information" appearance="stroke" size="sm">
                  {client?.name} belum punya SKU di katalog.
                </Alert>
              )}
              <SkuLinesEditor
                lines={lines}
                setLines={setLines}
                catalog={catalog}
                selectPlaceholder={
                  catalog.loading
                    ? 'Memuat katalog…'
                    : catalog.error
                      ? 'Katalog gagal dimuat'
                      : 'Pilih SKU'
                }
                extraOptions={catalog.error ? undefined : NEW_SKU_OPTIONS}
                onExtraOption={openNewSku}
                focusQty={focusQty}
                lineError={server.lineError}
                qtyMin={1}
                qtyPlaceholder="Qty"
                removeSize="icon-sm"
              />
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col gap-4" data-testid="po-review">
              <KVGrid
                items={[
                  ['Tipe PO', PO_TYPE_META[type].label],
                  ['Client', client?.name],
                  ['Hub tujuan', hubName],
                  type === 'REGULAR'
                    ? ['Vendor', vendor?.name]
                    : ['Diterima dari', returnedFrom.trim()],
                  ['Est. arrival', eta ? fmtDate(eta) : null],
                  ['Catatan', notes.trim() || null],
                  [
                    'Total',
                    `${filled.length} SKU · ${fmtQty(filled.reduce((a, l) => a + Number(l.qty), 0))} unit`,
                  ],
                ]}
              />
              <div className="flex flex-col divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
                {filled.map((l) => {
                  const s = catalog.skuBy.get(l.sku);
                  return (
                    <div
                      key={l.key}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className={codeCls}>
                          {l.sku}
                          {createdSkus.includes(l.sku) && (
                            <span className="ml-2 text-[11px] font-sans text-(--state-success-base)">
                              SKU baru
                            </span>
                          )}
                        </p>
                        <p className={`${metaCls} truncate`}>{s?.itemName}</p>
                      </div>
                      <span className="text-[13px] tabular-nums text-text-strong-950">
                        {fmtQty(Number(l.qty))} {s?.uomName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
      <SkuFormModal
        open={!!newSku && !!client}
        initialClient={client}
        lockedClient
        stacked
        initialSku={newSku?.code}
        onClose={() => setNewSku(null)}
        onSaved={onSkuCreated}
      />
    </>
  );
};

export default PurchaseOrderFormModal;
